/* eslint-disable camelcase */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SendCryptoScreensFlow } from '../../constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import Input from '../../../../components/Input';
import { USER_DETAILS_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { PHONE_TRANSACTION_VERIFICATION_SEND_AUTHY } from '../../../../utilities/sharedMutations';
import { showAlert } from '../../../../utilities/notifications';
import { TRANSACTION_SEND } from '../../mutations';
import {
  errorCodeIsRiskAssessmentWarning,
  getMaskedPhoneNumber,
} from '../../../../utilities/helpers';
import { RECAPTCHA_EVENTS } from '../../../Onboarding/constants';
import { handleFormBlur } from '../../../../utilities/formsUtils';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import Countdown from '../../../../utilities/counter';
import RecaptchaModal from '../../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../../utilities/caching';

const phoneCountdown = new Countdown();

const VerifyTransaction = ({ openScreen, onCloseModal, options, onSent }) => {
  const { data: userData } = useQuery(USER_DETAILS_QUERY_KEY);
  const { t: translate } = useTranslation();
  const [timer, setTimer] = useState();
  const [form, setForm] = useState({
    code: '',
    totp: '',
    confirmed_phone_number: '',
    country_code: '',
  });

  const isHigherFeesApplied = options?.higherFeesApplied;

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const [totpFeatureEnabled] = useSetting('app_features', 'feature_allow_totp');

  const [enableTransactionOTP] = useSetting('app_features', 'feature_enable_transactions_otp');
  const [enableTransactionTOTP] = useSetting('app_features', 'feature_enable_transactions_totp');

  const isTOTP =
    userData?.user?.totp_enabled &&
    totpFeatureEnabled &&
    userData?.user?.totp_active &&
    enableTransactionTOTP;

  const { coin, recipient, source, fees, amount } = options;

  const continueBtnDisabled = useMemo(() => {
    if (enableTransactionOTP && isTOTP) {
      return !form.code || !form.totp;
    }
    if (isTOTP) {
      return !form.totp;
    }
    return !form.code;
  }, [enableTransactionOTP, isTOTP, form]);

  const { isLoading: isSendingTransaction, mutate: sendTransaction } = useMutation(
    TRANSACTION_SEND,
    {
      onSettled(_, error) {
        if (!error) {
          showAlert({
            message: translate('send_transaction_success_message')
              .replace('__AMOUNT__', amount)
              .replace('__COIN__', coin.toUpperCase()),
            type: 'success',
          });
          onSent();
          onCloseModal();
        } else if (errorCodeIsRiskAssessmentWarning(error.errorCode)) {
          showAlert({
            message: error.message,
            type: 'warning',
          });
        } else {
          showAlert({ message: error.message, type: 'danger' });
        }
      },
    }
  );

  const { isLoading, mutate: send2faCode } = useMutation(
    PHONE_TRANSACTION_VERIFICATION_SEND_AUTHY,
    {
      onSettled(_, error) {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          phoneCountdown.start();
        }
      },
    }
  );

  const handleSend2faCode = async () => {
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      send2faCode({ authy_id: userData.user.authy_id, user_id: userData.user.id, token });
    } catch (err) {
      return false;
    }
    return true;
  };

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    send2faCode({ authy_id: userData.user.authy_id, user_id: userData.user.id, token: tokenV2 });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (isHigherFeesApplied) {
      openScreen(SendCryptoScreensFlow.CONFIRM_NEW_AMOUNT_TRANSACTION, {
        ...options,
        activeVerification: true,
        otp: form.code,
        totp: form.totp,
      });
    } else {
      sendTransaction({
        amount: fees?.sendAmount,
        coin,
        source,
        recipient,
        otp: form.code,
        totp: form.totp,
        currencyId: options?.coin_id,
      });
    }
  };

  useEffect(() => {
    phoneCountdown.bind(setTimer);

    if (enableTransactionOTP) {
      handleSend2faCode();
    }

    setForm({
      code: form.code,
      confirmed_phone_number: userData.user.country_code + userData.user.phone_number,
      country_code: userData.user.country_code,
    });

    return () => {
      phoneCountdown.stop();
    };
  }, []);

  const renderFooter = () => (
    <>
      <button
        className="btn btn-primary w-100 py-3"
        type="button"
        loading={isSendingTransaction ? 'true' : 'false'}
        disabled={continueBtnDisabled}
        onClick={onSubmit}>
        {translate('verify')}
      </button>
    </>
  );

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
  };

  return (
    <>
      <ModalBodyLayout
        withBack
        renderFooter={renderFooter}
        title={translate('verify_transaction_label')}
        onPrevClick={() => openScreen(SendCryptoScreensFlow.CONFIRM_TRANSACTION, options)}>
        <div className="d-flex flex-fill flex-column justify-content-start align-content-center h-100">
          {enableTransactionOTP && (
            <Input
              subLabel={`${translate('confirm_phone_number_message_label_nosec').replace(
                '%s',
                getMaskedPhoneNumber(form?.confirmed_phone_number, form?.country_code)
              )} ${timer} ${translate('email_verification_subtext_label_sec')}`}
              value={form.code}
              name="code"
              type="number"
              onChange={handleChange}
              actionLabel={translate('resend_button_label')}
              actionClick={handleSend2faCode}
              actionDisabled={isLoading || timer > 0}
              placeholder={translate('whitelist_verify_code_phone_number')}
              label={translate('whitelist_verify_code_phone_number')}
              groupClassName="mb-4"
              withFloatingLabel
              withSubLabel
            />
          )}

          {isTOTP && (
            <div className={`form-group ${enableTransactionOTP ? 'mt-0' : 'mt-4'}`}>
              <input
                type="number"
                className="form-control-input"
                placeholder={translate('whitelist_verify_code_totp')}
                onChange={handleChange}
                onBlur={handleFormBlur}
                onFocus={clearError}
                name="totp"
                autoComplete="off"
                required
              />
              <label className="floating-label">{translate('whitelist_verify_code_totp')}</label>
              <label className="error-label">{translate('verification_code_error_label')}</label>
            </div>
          )}
        </div>
      </ModalBodyLayout>
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default VerifyTransaction;

VerifyTransaction.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.object,
  onCloseModal: PropTypes.func.isRequired,
  onSent: PropTypes.func,
};

VerifyTransaction.defaultProps = {
  options: null,
  onSent: () => {},
};
